import { ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { get } from 'lodash';
import DateUtil from 'client/util/DateUtil';
import MspState from 'client/components/models/MspState';
import OptimizelyApi, { experiments, pageViewEvents } from 'client/GrowthEngineering/OptimizelyApi';
import store from 'client/store/index';

let experimentsData = null;

const growthModule = StoreModuleMap[ItemTypeConsts.growth];

const settings = store.state.SettingsModule;
const { betaFeatures } = store.state.FeatureFlagsModule;

const orgCreatedDate = () => store.getters['OrganizationsCollection/getOrgCreatedDate'];

const {
  landingPage,
  optimizelyAudienceTester,
} = experiments;

const getOrgCreatedAfterExperimentStartDate = (experiment) => {
  try {
    if (!experiment || !experiment.startDate) return false;

    const experimentStartDate = new Date(experiment.startDate);
    const orgCreatedAfterStartDate = !!DateUtil.isAfterDate(orgCreatedDate(), experimentStartDate);
    return orgCreatedAfterStartDate;
  } catch (e) {
    // just return false and move on
    return false;
  }
};

/**
 * Get the custom attributes to use for audience targeting in Optimizely
 * @param Object currentExperiments - default to experiments, used so tests
 * can pass in different experiments
 */
const initializeCustomAttributeValues = (currentExperiments = experiments) => {
  const email = get(settings, 'user.email', '');
  const isJumpCloudEmail = Boolean(email.toLowerCase().indexOf('@jumpcloud.com') >= 0);

  // for onboarding experiments for new orgs within 10 days from registration
  const isLessThan10DaysOld = Boolean(!DateUtil.moreThanDaysBeforeToday(orgCreatedDate(), 10));

  // for onboarding experiments for new orgs within 30 days from registration
  const isLessThan30DaysOld = Boolean(!DateUtil.moreThanDaysBeforeToday(orgCreatedDate(), 30));

  // Optimizely has a device audience for mobile
  // Mostly we care about small screen sizes
  // we may want to have these for different screen sizes in the future
  const isSmallScreen = Boolean(window.screen.width < 768);

  const { isAnMsp } = MspState;

  // orgs created after the start of experiments will be eligible
  window.eligibleExperiments = [];

  Object.keys(currentExperiments).forEach((experimentKey) => {
    const experiment = currentExperiments[experimentKey];
    // If no eligibilityCheck function, default to true
    const experimentEligibilityCheckFunction = experiment.eligibilityCheck || (() => true);
    const passedEligibilityCheck = experimentEligibilityCheckFunction();
    const orgCreatedAfterStartDate = getOrgCreatedAfterExperimentStartDate(experiment);

    if (orgCreatedAfterStartDate && passedEligibilityCheck) {
      window.eligibleExperiments.push(experiment.name);
    }
  });

  const attributes = {
    isAnMsp,
    isJumpCloudEmail,
    isLessThan10DaysOld,
    isLessThan30DaysOld,
    isSmallScreen,
  };

  OptimizelyApi.setCustomAttributes(attributes);
};

/**
 * Helper functions for tracking Optimizely page view events
 */
const trackAdminPortalConsoleView = () => {
  OptimizelyApi.trackPageViewEvent(pageViewEvents.adminPortalConsole);
};

const trackNotMspNorCommandRunner = () => {
  OptimizelyApi.trackPageViewEvent(pageViewEvents.notMspNorCommandRunner);
};

const trackWindowsAppModal = () => {
  OptimizelyApi.trackPageViewEvent(pageViewEvents.windowsAppModal);
};

const trackSignupAdminPortalCrossoverView = () => {
  OptimizelyApi.trackPageViewEvent(pageViewEvents.signupAdminPortalCrossover);
};

const trackLdapPageView = () => {
  OptimizelyApi.trackPageViewEvent(pageViewEvents.ldapPage);
};

const shouldShowHomeAsLandingPage = () => {
  // Orgs without feature flag should see users page
  let showHomeAsLandingPage = false;
  if (betaFeatures.homePage) {
    // Orgs created before the experiment date should see the home page
    showHomeAsLandingPage = true;
    const homeStartDate = new Date(landingPage.startDate);

    // Orgs created after the launch of the home page should get the experiment
    if (DateUtil.isAfterDate(orgCreatedDate(), homeStartDate)) {
      const variation = OptimizelyApi.getVariation(landingPage);
      if (variation === landingPage.variations.user) showHomeAsLandingPage = false;
    }
  }
  return showHomeAsLandingPage;

  // if the beta flag exists, do all the stuff otherwise show the old experience
};

/**
 * This is a special experiment to Test Optimizely Audiences
 * The tester can check local storage to see if the applied audiences are working for the org
 * Audience: All users, 100% to new variation
 */
const getOptimizelyAudienceTesterVariation = () => {
  try {
    const variation = OptimizelyApi.getVariation(optimizelyAudienceTester);
    const inAudience = Boolean(variation === optimizelyAudienceTester.variations.new);
    localStorage.setItem('jcInAudience', inAudience);
  } catch (e) {
    // just catch and move on
  }
};

/**
 * Helper function to get the experiment variation
 * @param {Object} experiment - The experiment object
 * @returns {boolean}
 */
const getExperimentVariation = (experiment) => {
  try {
    // if experiment is not valid, return false
    if (!experiment || !experiment.variations) return false;

    // unless experiment is set to display to older organizations,
    // return false if organization is created before experiment start date
    if (!experiment.showToOlderOrgs) {
      const orgCreatedAfterStartDate = getOrgCreatedAfterExperimentStartDate(experiment);
      if (!orgCreatedAfterStartDate) return false;
    }

    const variation = OptimizelyApi.getVariation(experiment);

    if (variation === 'Original') {
      return false;
    }

    // variation will return as an empty string if it is not active
    return !!variation;
  } catch (e) {
    // just return false and move on
    return false;
  }
};

const getExperimentVariationName = (experiment) => OptimizelyApi.getVariation(experiment);

const optimizelyCheck = () => window.optimizely && window.optimizely.get;

const bucketUserIntoExperiments = (growthData) => {
  const { optimizely } = window;
  if (optimizelyCheck()) {
    Object.entries(growthData.experimentStates).forEach(([experimentId, experimentState]) => {
      const variationState = get(experimentState, 'variationState', {});
      const optimizelyExperimentState = experimentsData[experimentId];

      // check that experiment is active for admin
      const isActive = OptimizelyApi.getIsActive(experimentState);

      if (isActive
        && optimizelyExperimentState.weightDistributions
        && optimizelyExperimentState.weightDistributions.findIndex(
          element => element.entityId === variationState.variationId,
        ) >= 0) {
        const experimentToPush = {
          type: 'bucketVisitor',
          experimentId,
          variationId: variationState.variationId,
        };
        optimizely.push(experimentToPush);
      } else if (!optimizelyExperimentState.weightDistributions) {
        // remove experiment from growthData
        store.dispatch(`${growthModule}/removeExperimentFromOrgGrowthData`, experimentId);
      }
    });
  }
};

const checkOptimizelyAgainstGrowthData = (currentGrowthData) => {
  const { optimizely } = window;
  if (!optimizelyCheck()) return currentGrowthData;

  if (!experimentsData) {
    experimentsData = get(optimizely.get('data'), 'experiments', {});
  }

  // Filter GrowthData experiments against the current experiments returned from Optimizely
  const filteredData = Object.entries(currentGrowthData.experimentStates)
    .filter(([experimentId]) => {
      const optimizelyExperimentState = experimentsData[experimentId];
      return optimizelyExperimentState;
    });

  // override the previous experimentStates in growthData with the filtered experiments
  const growthData = {
    ...currentGrowthData,
    experimentStates: Object.fromEntries(filteredData),
  };

  return growthData;
};

const checkOptimizelyAndBucketUserIntoExperiments = (data) => {
  if (!optimizelyCheck()) return data;

  const growthData = checkOptimizelyAgainstGrowthData(data);
  bucketUserIntoExperiments(growthData);
  return growthData;
};

export default {
  checkOptimizelyAndBucketUserIntoExperiments,
  initializeCustomAttributeValues,
  getExperimentVariation,
  getExperimentVariationName,
  getOptimizelyAudienceTesterVariation,
  shouldShowHomeAsLandingPage,
  trackAdminPortalConsoleView,
  trackLdapPageView,
  trackNotMspNorCommandRunner,
  trackSignupAdminPortalCrossoverView,
  trackWindowsAppModal,
};
